import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { ShoppingBag, ArrowUpRight } from "lucide-react";

export default function ShopPage() {
    const [products, setProducts] = useState([]);
    const [filter, setFilter] = useState("all");
    const { addItem } = useCart();
    const { settings } = useSettings();

    useEffect(() => {
        api.get("/products", { params: { active_only: true } }).then((r) => setProducts(r.data));
    }, []);

    if (settings.shop_enabled === false) return <Navigate to="/" replace />;

    const categories = ["all", ...Array.from(new Set(products.map((p) => p.category)))];
    const filtered = filter === "all" ? products : products.filter((p) => p.category === filter);

    return (
        <div className="min-h-screen pt-32 pb-32 grain" data-testid="shop-page">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="text-overline">Shop</div>
                <h1 className="font-serif-display text-5xl sm:text-7xl text-[#3D0C1F] mt-3 leading-[0.95]">Take Tresses <span className="italic gold-text">home</span>.</h1>
                <p className="text-[#7A5262] mt-4 max-w-xl">Salon-grade tools and rituals — curated by our master stylists.</p>

                <div className="flex flex-wrap gap-2 mt-12 border-b border-[#6B1535]/15 pb-1">
                    {categories.map((c) => (
                        <button key={c} onClick={() => setFilter(c)}
                            className={`px-5 py-3 text-xs uppercase tracking-[0.22em] transition-colors border-b-2 ${filter === c ? "border-[#C29A5B] text-[#8F622C]" : "border-transparent text-[#7A5262] hover:text-[#3D0C1F]"}`}
                            data-testid={`shop-filter-${c}`}>{c === "all" ? "All Products" : c}</button>
                    ))}
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
                    {filtered.map((p) => (
                        <div key={p.id} className="card-luxe grain group flex flex-col" data-testid={`shop-product-${p.id}`}>
                            {p.image && <div className="aspect-square overflow-hidden">
                                <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>}
                            <div className="p-6 flex-1 flex flex-col">
                                <div className="text-overline">{p.category}</div>
                                <h3 className="font-serif-display text-2xl text-[#3D0C1F] mt-2">{p.name}</h3>
                                <p className="text-sm text-[#7A5262] mt-2 flex-1">{p.description}</p>
                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#6B1535]/10">
                                    <span className="font-serif-display text-2xl gold-text">S${p.price}</span>
                                    <span className="text-xs text-[#8A6574]">{p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</span>
                                </div>
                                <button
                                    onClick={() => addItem(p)}
                                    disabled={p.stock <= 0}
                                    className="btn-gold mt-5 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid={`add-cart-${p.id}`}>
                                    <ShoppingBag size={12} /> Add to Bag
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div className="text-center py-32 text-[#7A5262]">
                        <p>No products available right now.</p>
                        <Link to="/" className="btn-outline-gold mt-6 inline-flex">Back home <ArrowUpRight size={12} /></Link>
                    </div>
                )}
            </div>
        </div>
    );
}
